import axios, { type AxiosInstance } from 'axios'
import { parseISO } from 'date-fns'
import useApi from '@/composables/api'
import queryClient from '@/plugins/query'
import { QUERY_KEYS } from '@/constants/query'
import ENV from '@/constants/env'
import type {
    BreezeTokenClaims,
    ConnectOptions,
    HubAccessToken,
    HubApi,
    HubAuthResponse,
    HubBillingPeriod,
    HubConnection,
    HubScope,
    HubSession,
    HubUser,
    TithelyHub
} from '@/types/composables/tithelyHub'

/**
 * Tithely Hub host. Sourced from `VITE_HUB_HOST`, falling back to the production
 * dev host. The token exchange below talks to Hub directly from the browser.
 */
const HUB_HOST = ENV.hubHost || `https://hub.tithely.dev`

/** OAuth client id Hub expects for the session-token grant. */
const HUB_CLIENT_ID = `app`

/**
 * Normalised error for any failed step of the Breeze → Hub exchange. Carries the
 * HTTP status and Hub's machine-readable error code so callers can branch on the
 * documented failure modes (e.g. `access_denied`, `unsupported_grant_type`,
 * `verification_required`).
 */
class HubError extends Error {
    readonly step: string
    readonly status?: number
    readonly code?: string

    constructor(step: string, message: string, status?: number, code?: string) {
        super(message)
        this.name = `HubError`
        this.step = step
        this.status = status
        this.code = code
    }
}

// Dedicated client for Hub. Deliberately NOT the shared breeze axiosInstance:
// that one attaches the Breeze access_token cookie as Bearer and runs Breeze
// auth/refresh/logout interceptors — none of which should apply to Hub.
const hub: AxiosInstance = axios.create({
    baseURL: HUB_HOST,
    headers: { 'Content-Type': `application/json` }
})

const toHubError = (step: string, error: unknown): HubError => {
    if (axios.isAxiosError(error)) {
        const status = error.response?.status
        const data = error.response?.data as
            | { error?: string; error_description?: string; message?: string }
            | undefined
        const code = data?.error
        const message = data?.error_description ?? data?.message ?? data?.error ?? error.message
        return new HubError(step, message, status, code)
    }

    return new HubError(step, error instanceof Error ? error.message : String(error))
}

// Decode a JWT payload without verifying the signature (verification is Hub's job).
const decodeJwtPayload = (token: string): BreezeTokenClaims => {
    const segment = token.split(`.`)[1]
    if (!segment) {
        throw new HubError(`decode`, `Malformed Breeze token`)
    }
    const base64 = segment.replace(/-/g, `+`).replace(/_/g, `/`)
    const json = decodeURIComponent(
        atob(base64)
            .split(``)
            .map(char => `%${(`00` + char.charCodeAt(0).toString(16)).slice(-2)}`)
            .join(``)
    )
    return JSON.parse(json) as BreezeTokenClaims
}

/**
 * Add `months` to `date` in UTC, clamping to the last day of the target month
 * when the anchor day doesn't exist there (e.g. a 31st anchor → Feb 28). This
 * mirrors how Stripe rolls a billing anchor forward, and — because every
 * boundary is derived from the original anchor rather than the previous one —
 * the billing day is preserved across short months (Feb 28 → Mar 31, not
 * Mar 28). Works purely in UTC so the result is timezone-independent.
 */
const addUtcMonthsClamped = (date: Date, months: number): Date => {
    const absoluteMonth = date.getUTCMonth() + months
    const targetYear = date.getUTCFullYear() + Math.floor(absoluteMonth / 12)
    const targetMonth = ((absoluteMonth % 12) + 12) % 12
    // Day 0 of the following month == last day of the target month.
    const daysInTargetMonth = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate()
    const day = Math.min(date.getUTCDate(), daysInTargetMonth)
    return new Date(
        Date.UTC(
            targetYear,
            targetMonth,
            day,
            date.getUTCHours(),
            date.getUTCMinutes(),
            date.getUTCSeconds(),
            date.getUTCMilliseconds()
        )
    )
}

/**
 * The current org's billing cycle, derived from its `stripeBilledFrom` anchor.
 * Prefer the org matching `organizationId` (the one usage is scoped to); fall
 * back to the session's `currentOrganizationId`.
 *
 * Stripe bills on the same day each month, so we take the anchor's day-of-month
 * and roll it forward to the cycle containing "now": `from` is the most recent
 * billing day on/before now, `to` the next one. Deriving both boundaries from
 * the original anchor keeps the billing day stable across short months. The SMS
 * quota resets each cycle, which is why this window (not the calendar month) is
 * what "texts remaining" counts against. Returns null when the org has no
 * billing anchor, letting callers fall back.
 */
const getBillingPeriod = (user: HubUser, organizationId: string): HubBillingPeriod | null => {
    const orgs = user.organizations ?? []
    const org = orgs.find(o => o.id === organizationId) ?? orgs.find(o => o.id === user.currentOrganizationId)
    const anchorIso = org?.billing?.stripeBilledFrom
    if (!anchorIso) {
        return null
    }

    const anchor = parseISO(anchorIso)
    const now = new Date()

    // Estimate whole cycles elapsed, then correct so that `from <= now < to`.
    // Comparisons use absolute instants (getTime), so this stays timezone-safe.
    let cycles = (now.getUTCFullYear() - anchor.getUTCFullYear()) * 12 + (now.getUTCMonth() - anchor.getUTCMonth())
    while (addUtcMonthsClamped(anchor, cycles).getTime() > now.getTime()) {
        cycles -= 1
    }
    while (addUtcMonthsClamped(anchor, cycles + 1).getTime() <= now.getTime()) {
        cycles += 1
    }

    return {
        from: addUtcMonthsClamped(anchor, cycles).toISOString(),
        to: addUtcMonthsClamped(anchor, cycles + 1).toISOString()
    }
}

/** Step 2 — exchange the Breeze JWT for a Hub session token. */
const createSession = async (breezeToken: string): Promise<HubSession> => {
    try {
        const { data } = await hub.post<HubSession>(`/auth/token/breeze`, { token: breezeToken })
        return data
    } catch (caught) {
        throw toHubError(`session`, caught)
    }
}

/** Step 3 — exchange the session token for a scoped OAuth access token. */
const createAccessToken = async (sessionToken: string, scope: HubScope): Promise<HubAccessToken> => {
    try {
        const { data } = await hub.post<{
            token_type: string
            expires_in: number
            access_token: string
        }>(`/auth/access_token`, {
            grant_type: `session_token`,
            client_id: HUB_CLIENT_ID,
            scope,
            session_token: sessionToken
        })

        return {
            accessToken: data.access_token,
            tokenType: data.token_type,
            expiresIn: data.expires_in
        }
    } catch (caught) {
        throw toHubError(`access-token`, caught)
    }
}

const useTithelyHub = (): TithelyHub => {
    const { api } = useApi()

    /**
     * Step 1 — obtain the raw Breeze SSO JWT.
     *
     * The `/tithely/sso` endpoint returns a redirect link of the form
     * `…/login/sso?breeze=<JWT>&continue=…`; the JWT lives in the `breeze` param.
     */
    const getBreezeToken = async (): Promise<string> => {
        const { data } = await api.tithelyAuth.tithelySso({ alias: 1 })

        const link = data?.link
        if (!link) {
            throw new HubError(`breeze-token`, `SSO endpoint returned no link`)
        }

        const breezeToken = new URL(link).searchParams.get(`breeze`)
        if (!breezeToken) {
            throw new HubError(`breeze-token`, `No "breeze" token found in SSO link`)
        }

        return breezeToken
    }

    /**
     * Run the full exchange (steps 1–3) and return the resulting tokens plus the
     * default organization id (the Breeze token's `aud` claim). Every step throws
     * a `HubError`, which the Vue Query layer surfaces as the query error.
     */
    const connect = async (options: ConnectOptions = {}): Promise<HubConnection> => {
        const { scope = `all` } = options

        const breezeToken = await getBreezeToken()
        const { aud } = decodeJwtPayload(breezeToken)
        const session = await createSession(breezeToken)
        const token = await createAccessToken(session.token, scope)

        const organizationId = String(aud)

        return {
            breezeToken,
            session,
            accessToken: token.accessToken,
            expiresIn: token.expiresIn,
            organizationId,
            billingPeriod: getBillingPeriod(session.user, organizationId)
        }
    }

    return { connect }
}

// The Hub connection is cached in the Vue Query cache under QUERY_KEYS.hubToken()
// — a single source of truth shared with useHubToken(). It survives component
// re-mounts and is shared across all useHubApi() call-sites. Deliberately kept in
// memory only (excluded from localStorage persistence in plugins/query.ts) so the
// bearer token is never exposed to XSS.
const HUB_TOKEN_EXPIRY_BUFFER_MS = 360_000

/**
 * Numeric staleTime for the cached connection: fresh until
 * `HUB_TOKEN_EXPIRY_BUFFER_MS` before the token's own expiry. query-core only
 * accepts a numeric staleTime, so we derive it per-call from whatever connection
 * is currently cached (0 on a cold cache, which forces a fetch).
 */
const hubTokenStaleTime = (): number => {
    const cached = queryClient.getQueryData<HubConnection>(QUERY_KEYS.hubToken())
    if (!cached) {
        return 0
    }
    const lifetimeMs = cached.expiresIn * 1_000
    // Refresh early, but never let the buffer exceed half the token's lifetime —
    // otherwise a short-lived token clamps staleTime to 0 and refetches every call.
    const buffer = Math.min(HUB_TOKEN_EXPIRY_BUFFER_MS, lifetimeMs / 2)
    return Math.max(0, lifetimeMs - buffer)
}

const useHubApi = (): HubApi => {
    const { connect } = useTithelyHub()

    /**
     * Returns the active HubConnection from the Vue Query cache, re-authenticating
     * transparently once the token is within HUB_TOKEN_EXPIRY_BUFFER_MS of expiry.
     * fetchQuery dedupes concurrent callers into a single in-flight connect().
     */
    const getConnection = (): Promise<HubConnection> =>
        queryClient.fetchQuery({
            queryKey: QUERY_KEYS.hubToken(),
            queryFn: () => connect(),
            staleTime: hubTokenStaleTime(),
            gcTime: Infinity,
            retry: false
        })

    const getAuth = async (): Promise<HubAuthResponse> => {
        const conn = await getConnection()
        try {
            const { data } = await hub.request<HubAuthResponse>({
                method: `GET`,
                url: `/auth`,
                headers: { Authorization: `Bearer ${conn.session.token}` }
            })
            return data
        } catch (caught) {
            throw toHubError(`auth`, caught)
        }
    }

    return {
        getConnection,
        getAuth
    }
}

export { HubError }
export { useHubApi }
export { hubTokenStaleTime }
export default useTithelyHub
